import { RADII, SPACING, STROKES } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import { useFocusEffect } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Linking, Platform, Pressable, ScrollView, Text, View, type TextStyle, type ViewStyle } from "react-native";

import {
  getInquiriesStats,
  listInquiries,
  setInquiryStatus,
  type InquiryChannel,
  type InquiryRow,
  type InquiryStatus,
  type InquiriesStats,
} from "../../../lib/inquiries";
import { useAdminListFilter } from "../../../lib/useAdminListFilter";
import { exportRowsAsCsv, fmtCellDate, todayDateForFilename } from "../../../lib/csvExport";
import {
  AdminButton,
  AdminEmpty,
  AdminError,
  AdminInput,
  AdminLoading,
  AdminPageHeader,
  FilterChip,
} from "../../../components/admin/ui";
import { StatStrip } from "../../../components/aspects/StatStrip";
import { StatusPill, type StatusPillTone } from "../../../components/aspects/StatusPill";

const THEME = PANE_THEMES.Admin;
const TZ = "America/New_York";

const STATUS_FILTERS: { key: InquiryStatus | "all"; label: string }[] = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "read", label: "Read" },
  { key: "responded", label: "Responded" },
  { key: "archived", label: "Archived" },
];

const CHANNEL_FILTERS: { key: InquiryChannel | "all"; label: string }[] = [
  { key: "all", label: "All" },
  { key: "company", label: "Company" },
  { key: "student", label: "Student" },
  { key: "partnership", label: "Partnership" },
  { key: "general", label: "General" },
];

function statusTone(status: InquiryStatus): StatusPillTone {
  if (status === "new") return "sage";
  if (status === "responded") return "amber";
  return "grey";
}

function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const sec = Math.floor(diff / 1000);
  if (sec < 45) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: TZ });
}

function formatFullTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: TZ })} · ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: TZ, timeZoneName: "short" })}`;
}

function truncate(s: string, n: number): string {
  const t = s.trim();
  return t.length <= n ? t : `${t.slice(0, n)}…`;
}

function channelLabel(c: InquiryChannel): string {
  return c.charAt(0).toUpperCase() + c.slice(1);
}

export default function AdminInquiriesScreen() {
  const [stats, setStats] = useState<InquiriesStats | null>(null);
  const [rows, setRows] = useState<InquiryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const filterUrl = useAdminListFilter({
    keys: ["q", "status", "channel", "sort"],
    defaults: { q: "", status: "all", channel: "all", sort: "newest" },
  });
  const search = filterUrl.get("q");
  const statusFilter = filterUrl.get("status") as InquiryStatus | "all";
  const channelFilter = filterUrl.get("channel") as InquiryChannel | "all";
  const sortVal = filterUrl.get("sort");

  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) filterUrl.set({ q: searchInput });
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [respondNotes, setRespondNotes] = useState("");
  const [respondingId, setRespondingId] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);
  const lastFilterKeyRef = useRef("");

  // Collapse any expanded row when the status/channel filter changes —
  // the expanded card may no longer be in the filtered set.
  useEffect(() => {
    setExpandedId(null);
    setRespondingId(null);
  }, [statusFilter, channelFilter]);

  const load = useCallback(async () => {
    const filterKey = `${statusFilter}:${channelFilter}`;
    if (lastFilterKeyRef.current !== filterKey) {
      hasLoadedRef.current = false;
      lastFilterKeyRef.current = filterKey;
    }
    if (!hasLoadedRef.current) setLoading(true);
    setErr(null);
    try {
      const [s, list] = await Promise.all([
        getInquiriesStats(),
        listInquiries({
          status: statusFilter === "all" ? undefined : statusFilter,
          channel: channelFilter === "all" ? undefined : channelFilter,
        }),
      ]);
      setStats(s);
      setRows(list);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load");
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, [statusFilter, channelFilter]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rows;
    if (q) {
      list = list.filter((r) => `${r.name} ${r.email} ${r.message} ${r.organization ?? ""} ${r.university ?? ""}`.toLowerCase().includes(q));
    }
    list = [...list].sort((a, b) => {
      if (sortVal === "oldest") return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      if (sortVal === "unread_first") {
        const ar = a.status === "new" ? 0 : 1;
        const br = b.status === "new" ? 0 : 1;
        if (ar !== br) return ar - br;
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
    return list;
  }, [rows, search, sortVal]);

  const onMarkRead = async (id: string) => {
    setActionErr(null);
    try { await setInquiryStatus(id, "read"); await load(); }
    catch (e) { setActionErr(e instanceof Error ? e.message : "Update failed"); }
  };
  const onArchive = async (id: string) => {
    setActionErr(null);
    try { await setInquiryStatus(id, "archived"); setExpandedId(null); await load(); }
    catch (e) { setActionErr(e instanceof Error ? e.message : "Update failed"); }
  };
  const onConfirmResponded = async (id: string) => {
    setActionErr(null);
    try { await setInquiryStatus(id, "responded", respondNotes.trim() || null); setRespondingId(null); setRespondNotes(""); await load(); }
    catch (e) { setActionErr(e instanceof Error ? e.message : "Update failed"); }
  };
  const openMailto = (row: InquiryRow) => {
    void Linking.openURL(`mailto:${row.email}?subject=${encodeURIComponent("Re: Your Coravu inquiry")}`);
  };

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ paddingBottom: 32 }}>
      <View style={{ padding: SPACING.lg, maxWidth: 1080 }}>
        <AdminPageHeader
          theme={THEME}
          eyebrow="Overview · Inquiries"
          title="Inquiries"
          subtitle="Company and public messages routed in from the contact page."
          actions={
            <AdminButton
              theme={THEME}
              label="Export CSV"
              variant="ghost"
              size="sm"
              onPress={() =>
                exportRowsAsCsv({
                  filename: `inquiries_${todayDateForFilename()}.csv`,
                  auditPage: "inquiries",
                  columns: [
                    { header: "Name", get: (r: InquiryRow) => r.name },
                    { header: "Email", get: (r: InquiryRow) => r.email },
                    { header: "Channel", get: (r: InquiryRow) => r.channel },
                    { header: "Status", get: (r: InquiryRow) => r.status },
                    { header: "Inquiry type", get: (r: InquiryRow) => r.inquiry_type ?? "" },
                    { header: "Organization", get: (r: InquiryRow) => r.organization ?? "" },
                    { header: "University", get: (r: InquiryRow) => r.university ?? "" },
                    { header: "Message", get: (r: InquiryRow) => r.message },
                    { header: "Created", get: (r: InquiryRow) => fmtCellDate(r.created_at) },
                    { header: "Response notes", get: (r: InquiryRow) => r.response_notes ?? "" },
                  ],
                  rows: filteredRows,
                })
              }
            />
          }
        />

        {err ? <View style={{ marginBottom: SPACING.md }}><AdminError theme={THEME} message={err} onRetry={() => void load()} /></View> : null}
        {actionErr ? <View style={{ marginBottom: SPACING.md }}><AdminError theme={THEME} message={actionErr} /></View> : null}

        <StatStrip
          theme={THEME}
          eyebrow="Inquiries"
          cells={[
            { label: "Total", value: stats?.total ?? 0 },
            { label: "New", value: stats?.new_count ?? 0, color: THEME.accent },
            { label: "Responded", value: stats?.responded_count ?? 0 },
            { label: "Archived", value: stats?.archived_count ?? 0 },
            { label: "This week", value: stats?.this_week ?? 0 },
          ]}
        />

        <View style={{ marginTop: SPACING.lg, gap: 10 }}>
          <FilterRow label="Status">
            {STATUS_FILTERS.map((f) => (
              <FilterChip key={f.key} theme={THEME} active={statusFilter === f.key} label={f.label} onPress={() => filterUrl.set({ status: f.key })} />
            ))}
          </FilterRow>
          <FilterRow label="Channel">
            {CHANNEL_FILTERS.map((f) => (
              <FilterChip key={f.key} theme={THEME} active={channelFilter === f.key} label={f.label} onPress={() => filterUrl.set({ channel: f.key })} />
            ))}
          </FilterRow>
          <AdminInput theme={THEME} value={searchInput} onChangeText={setSearchInput} placeholder="Search name, email, or message…" />
          <FilterRow label="Sort">
            <FilterChip theme={THEME} label="Newest" active={sortVal === "newest"} onPress={() => filterUrl.set({ sort: "newest" })} />
            <FilterChip theme={THEME} label="Oldest" active={sortVal === "oldest"} onPress={() => filterUrl.set({ sort: "oldest" })} />
            <FilterChip theme={THEME} label="Unread first" active={sortVal === "unread_first"} onPress={() => filterUrl.set({ sort: "unread_first" })} />
          </FilterRow>
        </View>

        <View style={{ marginTop: SPACING.md }}>
          {loading ? (
            <AdminLoading theme={THEME} label="Loading inquiries…" />
          ) : filteredRows.length === 0 ? (
            <AdminEmpty theme={THEME} title="No inquiries match these filters." />
          ) : (
            <View style={{ gap: 10 }}>
              {filteredRows.map((row) => {
                const isExpanded = expandedId === row.id;
                return (
                  <View key={row.id}>
                    <Pressable
                      onPress={() => {
                        setExpandedId((id) => (id === row.id ? null : row.id));
                        setActionErr(null);
                        setRespondingId(null);
                        setRespondNotes("");
                      }}
                      style={[
                        { padding: 14, borderRadius: RADII.md, backgroundColor: THEME.surfaceRaisedBg, borderWidth: STROKES.hairline, borderColor: isExpanded ? THEME.accentBorderHover : THEME.border } as ViewStyle,
                        Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                      ]}
                    >
                      <View style={{ flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
                        <StatusPill theme={THEME} label={channelLabel(row.channel)} tone="amber" showDot={false} />
                        <StatusPill theme={THEME} label={row.status} tone={statusTone(row.status)} />
                        <View style={{ flex: 1 }} />
                        <Text style={{ fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceDim }}>{formatRelative(row.created_at)}</Text>
                      </View>
                      <Text style={{ marginTop: 10, fontFamily: THEME.bodyMediumFont, fontSize: 14, color: THEME.textOnSurface }}>{row.name}</Text>
                      <Text style={{ marginTop: 1, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }}>{row.email}</Text>
                      {row.organization || row.university ? (
                        <Text style={{ marginTop: 2, fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceFaint }}>{row.organization ?? row.university}</Text>
                      ) : null}
                      <Text style={{ marginTop: 8, fontFamily: THEME.bodyFont, fontSize: 12.5, lineHeight: 18, color: THEME.textOnSurfaceDim }}>{truncate(row.message, 140)}</Text>
                    </Pressable>

                    {isExpanded ? (
                      <View style={{ marginTop: 8, padding: 16, borderRadius: RADII.md, backgroundColor: THEME.surfaceBg, borderWidth: STROKES.hairline, borderColor: THEME.border }}>
                        <SubLabel>Message</SubLabel>
                        <Text style={{ marginTop: 4, fontFamily: THEME.bodyFont, fontSize: 13, lineHeight: 19, color: THEME.textOnSurface }}>{row.message}</Text>

                        <SubLabel style={{ marginTop: 14 }}>Details</SubLabel>
                        <DetailRow k="Channel" v={row.channel} />
                        <DetailRow k="Inquiry type" v={row.inquiry_type ?? "—"} />
                        <DetailRow k="Organization" v={row.organization ?? "—"} />
                        <DetailRow k="University" v={row.university ?? "—"} />
                        <DetailRow k="Created" v={formatFullTimestamp(row.created_at)} />
                        <DetailRow k="Response notes" v={row.response_notes ?? "—"} />

                        <View style={{ marginTop: 14, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
                          {row.status === "new" ? <AdminButton theme={THEME} label="Mark as read" variant="outline" size="sm" onPress={() => void onMarkRead(row.id)} /> : null}
                          {respondingId !== row.id && row.status !== "responded" && row.status !== "archived" ? (
                            <AdminButton theme={THEME} label="Mark as responded" variant="outline" size="sm" onPress={() => { setRespondingId(row.id); setRespondNotes(row.response_notes ?? ""); }} />
                          ) : null}
                          {row.status !== "archived" ? <AdminButton theme={THEME} label="Archive" variant="outline" size="sm" onPress={() => void onArchive(row.id)} /> : null}
                          <AdminButton theme={THEME} label="Reply via email" size="sm" onPress={() => openMailto(row)} />
                        </View>

                        {respondingId === row.id ? (
                          <View style={{ marginTop: 14 }}>
                            <AdminInput theme={THEME} label="Response notes (optional)" value={respondNotes} onChangeText={setRespondNotes} multiline minHeight={70} placeholder="Internal notes — what you said, who you looped in" />
                            <View style={{ marginTop: 10, flexDirection: "row", gap: 8 }}>
                              <AdminButton theme={THEME} label="Confirm responded" size="sm" onPress={() => void onConfirmResponded(row.id)} />
                              <AdminButton theme={THEME} label="Cancel" variant="ghost" size="sm" onPress={() => { setRespondingId(null); setRespondNotes(""); }} />
                            </View>
                          </View>
                        ) : null}
                      </View>
                    ) : null}
                  </View>
                );
              })}
            </View>
          )}
        </View>
      </View>
    </ScrollView>
  );
}

function FilterRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
      <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11, color: THEME.textOnSurfaceDim }}>{label}:</Text>
      {children}
    </View>
  );
}

function SubLabel({ children, style }: { children: string; style?: TextStyle }) {
  return (
    <Text style={[{ fontFamily: THEME.bodyBoldFont, fontSize: 9, letterSpacing: 1.6, textTransform: "uppercase", color: THEME.textOnSurfaceDim }, style ?? null]}>
      {children}
    </Text>
  );
}

function DetailRow({ k, v }: { k: string; v: string }) {
  return (
    <View style={{ flexDirection: "row", marginTop: 4, gap: 8 }}>
      <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11, color: THEME.textOnSurfaceDim, minWidth: 110 }}>{k}</Text>
      <Text style={{ flex: 1, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurface }}>{v}</Text>
    </View>
  );
}
